import { useMutation } from '@apollo/client/react';
import { BadgeProps } from '@utilitywarehouse/hearth-react';
import { useEffect } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import {
  Appointment,
  AppointmentParticipant,
  ControlRequestedEvent,
} from '@generated/gql/graphql';
import { SnackbarVariant, useSnackbar } from '@hooks/useSnackbar';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  useTrackSignupRemoteControlAccepted,
  useTrackSignupRemoteControlRequestDenied,
  useTrackSignupRemoteControlRequested,
} from '@tracking/trackingEvents';
import {
  denyControlRemoteAppointment,
  giveControlRemoteAppointment,
  requestControlRemoteAppointment,
} from '@utils/queries/appointment';

interface UseScreenControlProps {
  appointment: Appointment;
  userId: string;
  controlRequest?: ControlRequestedEvent;
}
export const useScreenControl = ({
  appointment,
  userId,
  controlRequest,
}: UseScreenControlProps) => {
  const snackbar = useSnackbar();
  const requestControlDialog = useDialog(DialogID.APMT_REQUEST_CONTROL);
  const endDialog = useDialog(DialogID.APMT_END);
  const infoDialog = useDialog(DialogID.APMT_INFO);

  const [giveControl] = useMutation(giveControlRemoteAppointment);
  const [denyControl] = useMutation(denyControlRemoteAppointment);
  const [requestControl] = useMutation(requestControlRemoteAppointment);

  const trackControlRequested = useTrackSignupRemoteControlRequested();
  const trackControlRequestDenied = useTrackSignupRemoteControlRequestDenied();
  const trackControlRequestAccepted = useTrackSignupRemoteControlAccepted();
  const { trackingContexts } = useTrackingContext();

  const controller = appointment.controller;
  const participants = appointment.participants;

  const inControl = controller.id === userId;

  // temporary until we have a proper solution for colours
  //const colours = ['green', 'gold', 'cyan'];
  const colours = ['highlight', 'energy', 'pig'];
  const controllerColour = colours[
    participants.findIndex((u) => u.id === controller.id)
  ] as Partial<BadgeProps['colorScheme']>;

  useEffect(() => {
    if (controlRequest && controlRequest.toParticipant?.id === userId) {
      requestControlDialog.setOpen(true);
    }
  }, [
    controlRequest?.toParticipant?.id,
    userId,
    requestControlDialog,
    controlRequest,
  ]);

  const setController = (participant: AppointmentParticipant) => {
    if (appointment.controller.id === userId) {
      giveControl({
        variables: {
          id: appointment.id,
          requestorId: participant.id,
        },
      });
      trackControlRequestAccepted({}, { contexts: trackingContexts });
    } else if (participant.id === userId) {
      requestControl({
        variables: {
          id: appointment.id,
        },
      });
      trackControlRequested({}, { contexts: trackingContexts });
    }
  };

  const denyControlRequest = (request: ControlRequestedEvent) => {
    denyControl({
      variables: {
        id: appointment.id,
        requestorId: request.fromParticipant.id,
      },
    });
    trackControlRequestDenied({}, { contexts: trackingContexts });
  };

  const handleOverlayclick = () => {
    if (!inControl) {
      snackbar({
        variant: SnackbarVariant.Error,
        message: `${controller.name} has screen control`,
        preventDuplicate: true,
        persist: false,
      });
    }
  };

  return {
    inControl,
    controller,
    participants,
    controllerColour,
    setController,
    denyControlRequest,
    handleOverlayclick,
    endDialog,
    infoDialog,
    colours,
  };
};
